import React, {
  forwardRef,
  useState,
  useRef,
  useEffect,
  ChangeEvent,
} from "react";
import PropTypes from "prop-types";
import cx from "classnames";

import { ReactDefaultProps } from "@/types/react";
import { StringMapValidValue } from "@/types/StringMap";
import { mapChildrenIndexedByType } from "../../helper/map-children";
import { useUniqueId } from "../hooks/useUniqueId";

import { RadioButtonProvider } from "./RadioButtonContext";

import styles from "./RadioButton.scss";

declare const validRadioButtonGroupVariant: unique symbol;
export type RadioButtonGroupVariantValue = StringMapValidValue<
  typeof validRadioButtonGroupVariant
>;
const RadioButtonGroupVariants = {
  block: styles.groupBlock as RadioButtonGroupVariantValue,
  inline: styles.groupInline as RadioButtonGroupVariantValue,
};

export type RadioButtonGroupVariantMap = typeof RadioButtonGroupVariants;
export type RadioButtonGroupVariantKey = keyof RadioButtonGroupVariantMap;

export const RadioButtonGroupVariantValues = (Object.keys(
  RadioButtonGroupVariants
  // eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
) as RadioButtonGroupVariantKey[]).map((key) => RadioButtonGroupVariants[key]);

type RadioButtonGroupBaseProps = {
  variants?: RadioButtonGroupVariantValue | RadioButtonGroupVariantValue[];
  customClass?: string;
  value?: string;
  onChange?: (
    value: string,
    event?: React.ChangeEvent<HTMLInputElement>
  ) => void;
};

export type RadioButtonGroupProps = RadioButtonGroupBaseProps &
  Omit<
    React.ComponentPropsWithRef<"fieldset">,
    keyof RadioButtonGroupBaseProps
  >;

const defaultProps = {
  value: undefined,
  variants: undefined,
  customClass: undefined,
  onChange: () => undefined,
} as const;

const validatedDefaultProps: ReactDefaultProps<
  RadioButtonGroupBaseProps,
  typeof defaultProps
> = defaultProps;

// eslint-disable-next-line tree-shaking/no-side-effects-in-initialization
const RadioButtonGroupElementType = forwardRef<
  HTMLFieldSetElement,
  RadioButtonGroupProps
>((props, forwardedRef) => {
  const {
    children,
    variants,
    onChange = defaultProps.onChange,
    value,
    customClass,
    ...restProps
  } = props;
  const { id, name } = restProps;
  const fieldSetRef = useRef<HTMLFieldSetElement | null>(null);
  const [checkedId, setCheckedId] = useState("");
  const groupId = id || `${useUniqueId("RadioButton.Group", props) + 1}`;
  const groupName = name || `radio-${groupId}`;

  const setRef = (element: HTMLFieldSetElement) => {
    fieldSetRef.current = element;
    if (typeof forwardedRef === "function") forwardedRef(element);
    else if (forwardedRef)
      // eslint-disable-next-line no-param-reassign
      forwardedRef.current = element;
  };

  const getInputWithValue = () => {
    if (!value || !(fieldSetRef.current instanceof HTMLFieldSetElement))
      return undefined;
    const inputWithValue = fieldSetRef.current.querySelector(
      `input[value="${value}"]`
    );
    if (!(inputWithValue instanceof HTMLInputElement)) return undefined;
    return inputWithValue;
  };

  useEffect(() => {
    if (!value) return;
    const inputWithValue = getInputWithValue();
    if (inputWithValue) {
      setCheckedId(inputWithValue.id);
      onChange(value);
    }
  }, [value]);

  function onRadioClick(event: ChangeEvent<HTMLInputElement>) {
    setCheckedId(event.target.id);
    onChange(event.target.value, event);
  }

  return (
    <fieldset
      ref={setRef}
      className={cx(styles.fieldset, variants, customClass)}
      {...restProps}
    >
      {mapChildrenIndexedByType(children, (child, childIndexByType) => (
        <RadioButtonProvider
          value={{
            id: groupId,
            name: groupName,
            buttonId: `${childIndexByType + 1}`,
            checkedId,
            onRadioClick,
          }}
        >
          {child}
        </RadioButtonProvider>
      ))}
    </fieldset>
  );
});

RadioButtonGroupElementType.propTypes = {
  value: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  variants: PropTypes.oneOfType([
    PropTypes.oneOf(RadioButtonGroupVariantValues),
    PropTypes.arrayOf(
      PropTypes.oneOf(RadioButtonGroupVariantValues).isRequired
    ),
  ]),
  customClass: PropTypes.string,
  onChange: PropTypes.func,
};

RadioButtonGroupElementType.defaultProps = validatedDefaultProps;
RadioButtonGroupElementType.displayName = "RadioButton.Group";

export const RadioButtonGroup = Object.assign(RadioButtonGroupElementType, {
  Variant: RadioButtonGroupVariants,
});

export default RadioButtonGroup;
